import { useCallback, useEffect, useRef, useState } from "react";

import { formatCoords, normalizeCoords } from "./CatchFormValidation";

const isFiniteNumber = (value) => Number.isFinite(value);
const GEOLOCATION_PERMISSION_DENIED = 1;
const GEOLOCATION_POSITION_UNAVAILABLE = 2;
const GEOLOCATION_TIMEOUT = 3;
const PRIMARY_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};
const FALLBACK_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 15000,
  maximumAge: 120000,
};
const EMPTY_MAP_PICKER_DETAIL = {};

function isIphoneBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /iPhone/i.test(navigator.userAgent || "");
}

function isAndroidBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /Android/i.test(navigator.userAgent || "");
}

function isMobileLocationBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /Android|iPhone|iPad|iPod|Mobi|Mobile/i.test(
    navigator.userAgent || ""
  );
}

function requestCurrentPosition(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

async function getGeolocationPermissionState() {
  if (
    typeof navigator === "undefined" ||
    !navigator.permissions ||
    typeof navigator.permissions.query !== "function"
  ) {
    return "unknown";
  }

  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return ["granted", "prompt", "denied"].includes(status?.state)
      ? status.state
      : "unknown";
  } catch {
    return "unknown";
  }
}

function getGeolocationErrorCode(error) {
  const code = Number(error?.code);
  return Number.isFinite(code) ? code : 0;
}

function shouldRetryGeolocation(errorCode, permissionState = "unknown") {
  return (
    errorCode === 0 ||
    errorCode === GEOLOCATION_POSITION_UNAVAILABLE ||
    errorCode === GEOLOCATION_TIMEOUT ||
    (errorCode === GEOLOCATION_PERMISSION_DENIED &&
      permissionState !== "denied" &&
      isMobileLocationBrowser())
  );
}

export default function useCatchLocationState({
  pickedCoordinates,
  t,
  getCurrentMediaState,
  setDraftCatchMedia,
  draftExifDate,
  setDraftExifDate,
  draftExifTime,
  setDraftExifTime,
  initialCoordinates = "",
  initialLocation = "",
  initialIsLocationPublic = true,
  initialLocationMode = "",
  mapPickerSource = "createPost",
  mapPickerDetail = EMPTY_MAP_PICKER_DETAIL,
}) {
  const [coordinates, setCoordinates] = useState(initialCoordinates);
  const [isLocationPublic, setIsLocationPublic] = useState(
    initialIsLocationPublic
  );
  const [locationHint, setLocationHint] = useState("");
  const [location, setLocation] = useState(initialLocation);
  const [exifDate, setExifDate] = useState(draftExifDate || "");
  const [exifTime, setExifTime] = useState(draftExifTime || "");
  const [exifGps, setExifGps] = useState(null);
  const [useManualDateTime, setUseManualDateTime] = useState(false);
  const [locationMode, setLocationMode] = useState(
    initialLocationMode || (initialCoordinates ? "manual" : "none")
  );
  const [userClearedCoords, setUserClearedCoords] = useState(false);
  const manualDateTimeEditVersionRef = useRef(0);

  const applyDetectedExifDate = useCallback(
    (value) => {
      setExifDate(value);
      if (typeof setDraftExifDate === "function") {
        setDraftExifDate(value);
      }
    },
    [setDraftExifDate]
  );

  const applyDetectedExifTime = useCallback(
    (value) => {
      setExifTime(value);
      if (typeof setDraftExifTime === "function") {
        setDraftExifTime(value);
      }
    },
    [setDraftExifTime]
  );

  const resetSelectionDerivedLocationState = useCallback(() => {
    setExifDate("");
    setExifTime("");
    setExifGps(null);
    setUseManualDateTime(false);
    if (typeof setDraftExifDate === "function") {
      setDraftExifDate(null);
    }
    if (typeof setDraftExifTime === "function") {
      setDraftExifTime(null);
    }
  }, [setDraftExifDate, setDraftExifTime]);

  const clearLocationState = useCallback(() => {
    setCoordinates("");
    setLocation("");
    setLocationMode("none");
    setExifGps(null);
  }, []);

  const handleUseGps = useCallback(() => {
    if (!navigator.geolocation) {
      alert(t("catch_gps_not_supported"));
      return;
    }

    const isiPhone = isIphoneBrowser();
    const isAndroid = isAndroidBrowser();

    const applyResolvedPosition = async (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      const coordString = formatCoords(lat, lng);
      if (!coordString) {
        return;
      }
      setLocationHint("");
      setLocationMode("gps");
      setCoordinates(coordString);

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`
        );
        const data = await res.json();
        if (data?.display_name) {
          setLocation(data.display_name);
        }
      } catch {
        // ignore
      }
    };

    const showGeolocationError = (error, permissionState = "") => {
      const errorCode = getGeolocationErrorCode(error);

      if (errorCode === GEOLOCATION_PERMISSION_DENIED) {
        if (permissionState !== "denied" && isMobileLocationBrowser()) {
          alert(
            t(
              isAndroid
                ? "catch_gps_error_android_location_services_off"
                : isiPhone
                  ? "catch_gps_error_iphone_unavailable"
                  : "catch_gps_error_device_unavailable"
            )
          );
          return;
        }

        alert(
          t(
            isiPhone
              ? "catch_gps_error_iphone_permission"
              : isAndroid
                ? "catch_gps_error_android_permission"
                : "catch_gps_error_permission"
          )
        );
        return;
      }

      if (errorCode === GEOLOCATION_POSITION_UNAVAILABLE) {
        alert(
          t(
            isiPhone
              ? "catch_gps_error_iphone_unavailable"
              : isAndroid
                ? "catch_gps_error_android_unavailable"
                : "catch_gps_error_unavailable"
          )
        );
        return;
      }

      if (errorCode === GEOLOCATION_TIMEOUT) {
        alert(t("catch_gps_error_timeout"));
        return;
      }

      alert(t("catch_gps_error_unavailable"));
    };

    const handleGeolocationFailure = async (
      primaryError,
      permissionStateOverride = ""
    ) => {
      const permissionState =
        permissionStateOverride === "denied"
          ? permissionStateOverride
          : await getGeolocationPermissionState();
      const errorCode = getGeolocationErrorCode(primaryError);

      if (!shouldRetryGeolocation(errorCode, permissionState)) {
        showGeolocationError(primaryError, permissionState);
        return;
      }

      try {
        const fallbackPosition = await requestCurrentPosition(
          FALLBACK_GEOLOCATION_OPTIONS
        );
        await applyResolvedPosition(fallbackPosition);
        return;
      } catch (fallbackError) {
        const fallbackPermissionState = await getGeolocationPermissionState();
        showGeolocationError(fallbackError, fallbackPermissionState);
      }
    };

    if (typeof window !== "undefined" && window.isSecureContext === false) {
      alert(
        t(
          isiPhone
            ? "catch_gps_error_iphone_secure_context"
            : "catch_gps_error_secure_context",
          {
            defaultValue: isiPhone
              ? "Location access requires a secure connection on iPhone. Please reopen ReelitPro over HTTPS and try again."
              : "Location access requires a secure connection. Please reopen ReelitPro over HTTPS and try again.",
          }
        )
      );
      return;
    }

    if (!isiPhone) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          void applyResolvedPosition(position);
        },
        (error) => {
          void handleGeolocationFailure(error);
        },
        PRIMARY_GEOLOCATION_OPTIONS
      );
      return;
    }

    // Keep the more guarded iPhone flow, including permission-state
    // preflight and bounded retry behavior, because iPhone Safari / Home
    // Screen PWAs are more likely to fail or hang on a single strict request.
    (async () => {
      const permissionState = await getGeolocationPermissionState();

      if (permissionState === "denied" && isiPhone) {
        showGeolocationError(
          { code: GEOLOCATION_PERMISSION_DENIED },
          permissionState
        );
        return;
      }

      try {
        const position = await requestCurrentPosition(
          PRIMARY_GEOLOCATION_OPTIONS
        );
        await applyResolvedPosition(position);
      } catch (primaryError) {
        await handleGeolocationFailure(primaryError, permissionState);
      }
    })();
  }, [t]);

  const handlePickFromMap = useCallback(() => {
    const currentMediaState =
      typeof getCurrentMediaState === "function" ? getCurrentMediaState() : null;
    const resolvedMapPickerDetail =
      typeof mapPickerDetail === "function"
        ? mapPickerDetail()
        : mapPickerDetail;
    const safeMapPickerDetail =
      resolvedMapPickerDetail && typeof resolvedMapPickerDetail === "object"
        ? resolvedMapPickerDetail
        : EMPTY_MAP_PICKER_DETAIL;

    if (currentMediaState?.snapshotFile && typeof setDraftCatchMedia === "function") {
      setDraftCatchMedia(currentMediaState);
    }

    if (exifDate && typeof setDraftExifDate === "function") {
      setDraftExifDate(exifDate);
    }

    if (exifTime && typeof setDraftExifTime === "function") {
      setDraftExifTime(exifTime);
    }

    setLocationHint("");
    setLocationMode("map");
    window.dispatchEvent(
      new CustomEvent("goToMapPicker", {
        detail: {
          ...safeMapPickerDetail,
          source: mapPickerSource,
        },
      })
    );
  }, [
    exifDate,
    exifTime,
    getCurrentMediaState,
    mapPickerDetail,
    mapPickerSource,
    setDraftCatchMedia,
    setDraftExifDate,
    setDraftExifTime,
  ]);

  const handleStartManualCoordinates = useCallback(() => {
    setLocationHint("");
    setLocationMode("manual");
  }, []);

  const handleCancelManualCoordinates = useCallback(() => {
    setCoordinates("");
    setLocation("");
    setLocationHint("");
    setLocationMode("none");
    setUserClearedCoords(true);
  }, []);

  useEffect(() => {
    const onMapPicked = (event) => {
      const { lat, lng } = event.detail || {};
      if (lat == null || lng == null) {
        return;
      }

      const coordString = formatCoords(lat, lng);
      if (!coordString) {
        return;
      }
      setLocationHint("");
      setCoordinates(coordString);
      setLocation("");
      setLocationMode("map");
    };

    window.addEventListener("mapLocationPicked", onMapPicked);
    return () => window.removeEventListener("mapLocationPicked", onMapPicked);
  }, []);

  useEffect(() => {
    if (
      exifGps &&
      !coordinates &&
      locationMode === "none" &&
      !userClearedCoords
    ) {
      if (!isFiniteNumber(exifGps.lat) || !isFiniteNumber(exifGps.lng)) {
        return;
      }
      const coordString = formatCoords(exifGps.lat, exifGps.lng);
      if (!coordString) {
        return;
      }
      setCoordinates(coordString);
      setLocationMode("exif");
      setLocationHint(t("location_detected_from_photo"));
    }
  }, [coordinates, exifGps, locationMode, t, userClearedCoords]);

  useEffect(() => {
    if (
      pickedCoordinates &&
      locationMode === "none" &&
      !coordinates &&
      !userClearedCoords
    ) {
      setCoordinates(normalizeCoords(pickedCoordinates));
      setLocationMode("map");
    }
  }, [coordinates, locationMode, pickedCoordinates, userClearedCoords]);

  const handleCoordinatesChange = useCallback((event) => {
    const raw = event.target.value.replace(/\s+/g, "");
    setCoordinates(raw);

    if (raw === "") {
      setUserClearedCoords(true);
      setLocationHint("");
      setLocationMode("none");
      setLocation("");
    }
  }, []);

  const handleCoordinatesBlur = useCallback(() => {
    if (coordinates) {
      setCoordinates(normalizeCoords(coordinates));
    }
  }, [coordinates]);

  const handleSetLocationPublic = useCallback(() => {
    setIsLocationPublic(true);
    setLocationHint(t("catch_location_now_public"));
    setTimeout(() => setLocationHint(""), 1800);
  }, [t]);

  const handleSetLocationPrivate = useCallback(() => {
    setIsLocationPublic(false);
    setLocationHint(t("catch_location_now_private"));
    setTimeout(() => setLocationHint(""), 1800);
  }, [t]);

  const handleClearCoordinates = useCallback(() => {
    setCoordinates("");
    setUserClearedCoords(true);
    setLocationHint("");
    setLocationMode("none");
    setLocation("");
  }, []);

  // Apply EXIF-derived coords only when the field is genuinely empty at apply
  // time. Uses setCoordinates' functional updater so the empty-check is atomic
  // with respect to concurrent user typing; the other two guards
  // (locationMode !== "none", userClearedCoords) are checked against the
  // latest committed state via closure, which is the freshest React gives us
  // from a microtask caller. Returns true only when the write actually
  // landed, so the caller can emit the right telemetry event.
  const setCoordsFromExifIfEmpty = useCallback(
    ({ latitude, longitude }) => {
      if (!isFiniteNumber(latitude) || !isFiniteNumber(longitude)) {
        return false;
      }
      if (locationMode !== "none" || userClearedCoords) {
        return false;
      }
      const coordString = formatCoords(latitude, longitude);
      if (!coordString) {
        return false;
      }

      let applied = false;
      setCoordinates((prev) => {
        if (prev) {
          return prev;
        }
        applied = true;
        return coordString;
      });

      if (applied) {
        setLocationMode("exif");
        setLocationHint(t("location_detected_from_photo"));
      }

      return applied;
    },
    [locationMode, t, userClearedCoords]
  );

  const handleManualDateChange = useCallback(
    (event) => {
      manualDateTimeEditVersionRef.current += 1;
      setExifDate(event.target.value);
      setDraftExifDate(event.target.value);
    },
    [setDraftExifDate]
  );

  const handleManualTimeChange = useCallback(
    (event) => {
      manualDateTimeEditVersionRef.current += 1;
      setExifTime(event.target.value);
      setDraftExifTime(event.target.value);
    },
    [setDraftExifTime]
  );

  const getManualDateTimeEditVersion = useCallback(
    () => manualDateTimeEditVersionRef.current,
    []
  );

  return {
    coordinates,
    setCoordinates,
    isLocationPublic,
    setIsLocationPublic,
    locationHint,
    setLocationHint,
    location,
    setLocation,
    exifDate,
    setExifDate,
    exifTime,
    setExifTime,
    exifGps,
    setExifGps,
    useManualDateTime,
    setUseManualDateTime,
    locationMode,
    setLocationMode,
    handleUseGps,
    handlePickFromMap,
    handleStartManualCoordinates,
    handleCancelManualCoordinates,
    handleCoordinatesChange,
    handleCoordinatesBlur,
    handleSetLocationPublic,
    handleSetLocationPrivate,
    handleClearCoordinates,
    handleManualDateChange,
    handleManualTimeChange,
    getManualDateTimeEditVersion,
    applyDetectedExifDate,
    applyDetectedExifTime,
    resetSelectionDerivedLocationState,
    clearLocationState,
    setCoordsFromExifIfEmpty,
  };
}
